/**
 * The one icon for a file by its type — PDF, image, spreadsheet, archive,
 * anything else. Reads the MIME type first and falls back to the file name's
 * extension (a stored file's `content_type` is often a generic
 * `application/octet-stream`).
 */
import { File, FileArchive, FileImage, FileSpreadsheet, FileText, type LucideIcon } from 'lucide-react';

const SPREADSHEET_TYPES = new Set([
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
]);
const TEXT_DOCUMENT_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'text/plain',
]);
const ARCHIVE_TYPES = new Set(['application/zip', 'application/x-zip-compressed', 'application/x-7z-compressed']);

const BY_EXTENSION: Record<string, LucideIcon> = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  odt: FileText,
  txt: FileText,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
  webp: FileImage,
  heic: FileImage,
  svg: FileImage,
  csv: FileSpreadsheet,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  ods: FileSpreadsheet,
  zip: FileArchive,
  '7z': FileArchive,
};

export function fileTypeIcon(contentType?: string | null, fileName?: string | null): LucideIcon {
  const type = (contentType ?? '').toLowerCase();
  if (type.startsWith('image/')) return FileImage;
  if (type === 'application/pdf' || TEXT_DOCUMENT_TYPES.has(type)) return FileText;
  if (SPREADSHEET_TYPES.has(type)) return FileSpreadsheet;
  if (ARCHIVE_TYPES.has(type)) return FileArchive;
  const extension = fileName?.split('.').pop()?.toLowerCase();
  return (extension && fileName?.includes('.') && BY_EXTENSION[extension]) || File;
}
